import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

import type { ReadAnimal } from '@/services/api/subjectsService';

interface AnimalCardProps {
  animal: ReadAnimal;
  onEdit?: () => void;
}

export const AnimalCard = ({ animal, onEdit }: AnimalCardProps) => {
  const displayName = animal.name || animal.species || 'Animal';

  const hasAdditionalDetails = animal.description || animal.species;

  return (
    <div className="rounded border border-gray-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          <SparklesIcon className="h-5 w-5 mt-0.5 flex-shrink-0 text-gray-400" />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900">{displayName}</h3>

            {animal.involvement_description && (
              <p className="mt-1 text-sm text-gray-600">
                {animal.involvement_description}
              </p>
            )}

            {animal.species && animal.name && (
              <div className="mt-1 text-sm text-gray-500">{animal.species}</div>
            )}
          </div>
        </div>
        {onEdit && (
          <button
            onClick={onEdit}
            className="text-xs text-gray-400 hover:text-gray-600"
            type="button"
          >
            Edit
          </button>
        )}
      </div>

      {hasAdditionalDetails && (
        <Disclosure as="div" className="mt-3">
          {({ open }) => (
            <>
              <Disclosure.Button className="ml-7 flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900">
                <span>View Details</span>
                <ChevronDownIcon
                  className={clsx(
                    'h-4 w-4 transition-transform',
                    open && 'rotate-180',
                  )}
                />
              </Disclosure.Button>

              <Disclosure.Panel className="mt-3 ml-7 space-y-3">
                {animal.species && !animal.name && (
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Species
                    </div>
                    <div className="text-sm text-gray-900">
                      {animal.species}
                    </div>
                  </div>
                )}

                {animal.description && (
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Description
                    </div>
                    <div className="text-sm text-gray-700 leading-relaxed">
                      {animal.description}
                    </div>
                  </div>
                )}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      )}
    </div>
  );
};
